// Hideo - "Create Shorts from the script", without UI: suggests up to 3 Shorts from the script of a Long Form video,
// without AI. The button sits under the Script in the video dialog (src/components/video/ShortButton.vue): active only if
// the video's type is Long Form (formatKind 'h') and the script has enough text; otherwise it is faded and dashed, with
// the reason on hover (and on tap, on the phone). The suggestions window is src/components/dialogs/ShortsDialog.vue.
//
// How it finds the Shorts (active tab of the chosen script):
//  1. splits the text into sections (headings) and sentences;
//  2. tries every run of consecutive sentences 55–150 words long (about 22–60 seconds at 2.5 words per second);
//  3. scores them: strong opening (question, number, "did you know", "the secret"…), punchy ending, whole section,
//     length close to 40 seconds; takes points away from runs that depend on the rest ("as I was saying", "so",
//     "this…"), from greetings and calls to subscribe, and from intro, CTA or sources sections;
//  4. keeps the best 3 that don't overlap. It doesn't understand the meaning: the suggestions must be reread.
// The script can be Italian or English: the word lists below exist in both languages and the script's language is
// guessed from its most common words (shIsEnglish). Italian scripts are scored with the Italian lists only.
import { t } from '../i18n'
import { formatKind } from '../data/contentTypes'
import type { FormatName, Kind, ProfileData, Script, Video } from '../data/types'
import { esc as escHtml } from '../utils/html'
import { cleanHtml } from './editor/html'
import { seriesOf } from './formats'
import { plain } from './scripts'
import { WORDS_PER_SEC, countWords } from '../utils/words'

export const SH_MIN = 55, SH_MAX = 150, SH_IDEAL = 100, SH_WPS = WORDS_PER_SEC
/** Below this score a piece is not suggested */
export const SH_MIN_SCORE = 2
const SH_HOOK = /^(lo sapevi|sapevi|sai (che|perch[eé]|cosa|come)|ecco (perch[eé]|come|cosa|il)|il segreto|nessuno|la verit[aà]|attenzione|immagina|cosa succede|perch[eé]|come mai|quant[ioe]|qual [eè]|ti sei mai|non (crederai|ci crederai)|c'[eè] (un|una)|esiste|il motivo|il giorno in cui|tutti|mai)\b/i
const SH_NUM = /\d|\b(due|tre|quattro|cinque|sette|otto|nove|dieci|cento|mille|milion[ei]|miliard[ei])\b/i
const SH_DEP = /^(e|ma|per[oò]|quindi|inoltre|poi|infatti|anche|quest[oaie]|lui|lei|loro|qui|cos[iì]|allora|invece|oltretutto|dopo|intanto)\b/i
const SH_BACK = /(come (abbiamo|ho|hai) (detto|visto|gi[aà])|come dicevo|come detto|abbiamo visto|di cui (parlavo|abbiamo parlato)|nel prossimo|pi[uù] avanti|pi[uù] tardi|prima parte|in questo video|capitolo)/gi
const SH_CTA = /(iscriviti|iscrivetevi|campanella|link in descrizione|lascia(te)? un like|commenta(te)?|alla prossima|ciao a tutti|benvenut[ie]|bentornat[ie]|sponsor)/i
const SH_SKIP_HEAD = /(intro|introduzione|cta|chiusura|fonti|saluti|sponsor|conclusion)/i
const SH_STRONG = /(incredibil|assurd|pazzesc|segret|record|unic[oa]|enorm|peggior|miglior|mistero|vietat|impossibil)/gi
// English equivalents of the lists above
const SH_HOOK_EN = /^(did you know|you know (that|why|what|how)|here'?s (why|how|what|the)|the secret|nobody|no one|the truth|warning|imagine|what happens|why|how come|how (many|much)|what is|have you ever|you (won'?t|will not) believe|there'?s (a|an)|there (is|are)|the reason|the day|everyone|everybody|never)\b/i
const SH_NUM_EN = /\d|\b(two|three|four|five|six|seven|eight|nine|ten|hundred|thousand|millions?|billions?)\b/i
const SH_DEP_EN = /^(and|but|so(?=,)|therefore|also|then|plus|besides|actually|this|these|that|those|he|she|they|here|there|instead|meanwhile|after that|anyway)\b/i
const SH_BACK_EN = /(as (we|i|you) (said|saw|mentioned|already)|as i was saying|like i said|as mentioned|we('ve| have) seen|(that|which) (i|we) (talked|were talking|was talking) about|in the next|later on|first part|in this video|chapter)/gi
const SH_CTA_EN = /(subscribe|the bell|link in the description|leave a like|hit (the )?like|comment below|see you next time|hi everyone|hey (guys|everyone)|welcome( back)?|sponsor)/i
const SH_SKIP_HEAD_EN = /(intro|cta|outro|closing|sources|greetings|sponsor|conclusion|wrap.?up)/i
const SH_STRONG_EN = /\b(incredibl[ey]|insane|crazy|secrets?|record|unique|huge|enormous|worst|best|myster(y|ies|ious)|banned|forbidden|impossible)\b/gi
/** Word lists of one language: hook openings, numbers, dependent openings, back-references (g), greetings and calls to
 *  subscribe, sections to skip, strong words (g) */
interface ShLists {
  hook: RegExp
  num: RegExp
  dep: RegExp
  back: RegExp
  cta: RegExp
  skip: RegExp
  strong: RegExp
}
export const SH_LISTS: Record<'it' | 'en', ShLists> = {
  it: { hook: SH_HOOK, num: SH_NUM, dep: SH_DEP, back: SH_BACK, cta: SH_CTA, skip: SH_SKIP_HEAD, strong: SH_STRONG },
  en: { hook: SH_HOOK_EN, num: SH_NUM_EN, dep: SH_DEP_EN, back: SH_BACK_EN, cta: SH_CTA_EN, skip: SH_SKIP_HEAD_EN, strong: SH_STRONG_EN }
}
// Language of the script, from its most common short words: English only when they outnumber the Italian ones
const SH_EN_WORDS = /\b(the|and|you|your|is|are|of|to|that|this|with|what|it|was|for)\b/gi
const SH_IT_WORDS = /\b(il|lo|la|gli|le|di|che|non|per|una|sono|del|della|con|si)\b/gi
export const shIsEnglish = (txt: unknown): boolean => (String(txt).match(SH_EN_WORDS) || []).length > (String(txt).match(SH_IT_WORDS) || []).length

export const shWords = (txt: unknown): number => countWords(String(txt))   // String(): null counts as a word, like before

export interface ShSection {
  title: string
  paras: string[]
}
/** Sections { title, paras: [text] } from the script's HTML (headings H1–H6 = new section); clean = the HTML cleaning
 *  of the editor (cleanHtml, src/features/editor/html.ts) */
export function shSections(html: string, clean: (h: string) => string = cleanHtml): ShSection[] {
  const box = document.createElement('div')
  box.innerHTML = clean(html || '')
  const out: ShSection[] = []
  let cur: ShSection = { title: '', paras: [] }
  const push = (txt: unknown) => { const x = String(txt).replace(/\s+/g, ' ').trim(); if (x) cur.paras.push(x) }
  const walk = (el: Element) => {
    for (const n of el.childNodes) {
      if (n.nodeType === 3) { push(n.textContent); continue }
      if (n.nodeType !== 1) continue
      const e = n as Element
      if (/^H[1-6]$/.test(e.tagName)) {
        if (cur.title || cur.paras.length) out.push(cur)
        cur = { title: (e.textContent || '').trim(), paras: [] }
      } else if (/^(UL|OL|DIV|DETAILS|SECTION|BLOCKQUOTE)$/.test(e.tagName) && e.querySelector('p,li,h1,h2,h3,h4,div')) walk(e)
      else push(e.textContent)
    }
  }
  walk(box)
  out.push(cur)
  return out.filter(s => s.paras.length)
}
/** HTML of the active tab of a script, or null without the script */
export function shScriptHtml(d: Pick<ProfileData, 'scripts'>, id: string | undefined): string | null {
  const sc = d.scripts.find(x => x.id === id)
  if (!sc) return null
  const tab = (sc.tabs || []).find(tb => tb.id === sc.activeTab) || (sc.tabs || [])[0]
  return tab ? tab.html : (sc.html || '')
}

/** A sentence of the script: text, words, section and paragraph, section title; first / last of its section */
export interface ShSentence {
  text: string
  w: number
  si: number
  pi: number
  title: string
  first?: boolean
  last?: boolean
}
/** A suggested Short: sentences i…j, words, seconds, score, reasons, title; done = already created (ShortsDialog.vue) */
interface ShProposal {
  i: number
  j: number
  w: number
  sec: number
  score: number
  why: string[]
  title: string
  sentences: string[]
  done?: boolean
}
/** The best 3 non-overlapping Shorts of the script with a score of at least SH_MIN_SCORE */
export function shFindShorts(html: string, clean?: (h: string) => string): ShProposal[] {
  const secs = shSections(html, clean), S: ShSentence[] = []
  const L = SH_LISTS[shIsEnglish(secs.map(s => s.paras.join(' ')).join(' ')) ? 'en' : 'it']
  secs.forEach((s, si) => s.paras.forEach((p, pi) => {
    (p.match(/[^.!?…]+[.!?…]+["»”')]*|[^.!?…]+$/g) || []).map(x => x.trim()).filter(Boolean).forEach(text =>
      S.push({ text, w: shWords(text), si, pi, title: s.title }))
  }))
  S.forEach((s, i) => { s.first = i === 0 || S[i - 1].si !== s.si; s.last = i === S.length - 1 || S[i + 1].si !== s.si })
  const cands: ShProposal[] = []
  for (let i = 0; i < S.length; i++) {
    let w = 0
    for (let j = i; j < S.length; j++) {
      w += S[j].w
      if (w > SH_MAX) break
      if (w < SH_MIN) continue
      cands.push(shScore(S, i, j, w, L))
    }
  }
  cands.sort((a, b) => b.score - a.score)
  const pick: ShProposal[] = []
  for (const c of cands) {
    if (pick.length === 3 || c.score < SH_MIN_SCORE) break
    if (pick.every(p => c.j < p.i || c.i > p.j)) pick.push(c)
  }
  return pick
}
/** Score of the run of sentences i…j (w words); L = word lists of the script's language (SH_LISTS) */
export function shScore(S: ShSentence[], i: number, j: number, w: number, L: ShLists = SH_LISTS.it): ShProposal {
  const part = S.slice(i, j + 1), first = part[0], last = part[part.length - 1], all = part.map(s => s.text).join(' ')
  let score = 0
  const why: string[] = []
  if (/\?\s*["»”')]*$/.test(first.text)) { score += 3; why.push(t('Starts with a question')) }
  else if (L.hook.test(first.text)) { score += 3; why.push(t('Strong opening')) }
  if (L.num.test(first.text) && !L.dep.test(first.text)) { score += 1.5; if (!why.length) why.push(t('Starts with a number')) }
  if (first.w <= 14) score += 1
  if (L.dep.test(first.text)) score -= 3
  const back = (all.match(L.back) || []).length
  score -= Math.min(4, back * 2)
  if (!L.dep.test(first.text) && !back) why.push(t('Stands on its own'))
  if (L.cta.test(all)) score -= 3
  if (first.first) { score += 2; if (last.last && first.si === last.si) { score += 1; why.push(t('A whole section')) } }
  const crossings = last.si - first.si
  score -= crossings * 1.5
  if (part.some(s => L.skip.test(s.title))) score -= 2
  if (/[!?]\s*["»”')]*$/.test(last.text) || last.w <= 12) { score += 1; why.push(t('Punchy ending')) }
  score += Math.min(2, (all.match(L.strong) || []).length * 0.5)
  score += Math.max(-2, 2 - Math.abs(w - SH_IDEAL) / 30)
  if (Math.abs(w - SH_IDEAL) <= 20) why.push(t('Ideal length'))
  const head = first.first && first.title && !L.skip.test(first.title) ? first.title : first.text.replace(/[.!…]+$/, '')
  const title = head.length > 60 ? head.slice(0, 57).replace(/\s+\S*$/, '') + '…' : head
  return { i, j, w, sec: Math.round(w / SH_WPS), score, why, title, sentences: part.map(s => s.text) }
}

// ===== The button and the window =====
/** Why "Create Shorts from the script" cannot be used ('' = it can): kind = the video's type, html = its script (null
 *  without one) */
export function shortWhy(kind: Kind, html: string | null): string {
  const w = html == null ? 0 : shWords(plain(html))
  return kind !== 'h' ? t('To use this feature the video must be horizontal (Long Form)')
    : html == null ? t('Create the video script first: the Shorts are taken from it')
      : w < SH_MIN ? t('The script is too short: you need at least {min} words (now {n})', { min: SH_MIN, n: w }) : ''
}
/** The open video a Short is taken from: its id (null = not saved yet), title as typed, project and format */
interface ShortFrom {
  id: string | null
  title: string
  projectId: string
  seriesId: string
}
/** "Create this Short": a new Short Form video ("In Script", same project, the long video's format if it belongs to that
 *  project, notes pointing to the long video) with its own script (Hook / Body / Ending / CTA, in the UI language; "Hook"
 *  and "CTA" are the same in both languages). k = the suggestion's number (ids). Adds both to the data. */
export function createShort(d: ProfileData, p: ShProposal, k: number, from: ShortFrom, now = Date.now(), esc: (s: unknown) => string = escHtml): { video: Video; script: Script } {
  const vid = 'v_' + now, sid = 'sc_' + now + '_s' + k, proj = from.projectId
  const series = (seriesOf(d, from.seriesId) || {} as { projectId?: string }).projectId === proj ? from.seriesId : ''
  const fmt = (d.settings.formats.find(f => formatKind(f) === 'v') || 'Short Form') as FormatName   // the first vertical type
  const longTitle = from.title.trim() || t('long video')
  const ss = p.sentences, html = (ss.length >= 3
    ? `<h2>Hook</h2><p>${esc(ss[0])}</p><h2>${t('Body')}</h2><p>${esc(ss.slice(1, -1).join(' '))}</p><h2>${t('Ending')}</h2><p>${esc(ss[ss.length - 1])}</p>`
    : `<h2>Hook</h2><p>${esc(ss[0])}</p>` + (ss[1] ? `<h2>${t('Ending')}</h2><p>${esc(ss[1])}</p>` : ''))
    + `<h2>CTA</h2><p>${t('The full video is on the channel.')}</p>`
  const video = { id: vid, title: p.title, link: '', scriptId: sid, status: 'In Script', format: fmt, priority: 'Media', reelProva: 'No',
    publishDate: '', publishTime: '', thumbnail: '', voiceNote: '', checklist: {}, projectId: proj, seriesId: series,
    notes: from.id ? t('Short taken from {ref}', { ref: `@[video:${from.id}]` }) : t('Short taken from "{title}"', { title: longTitle }) } as Video
  const script = { id: sid, title: p.title, videoId: vid, mode: 'visual', updatedAt: now, tabs: [{ id: 'tb_' + now + '_s' + k, name: t('First draft'), html }] } as Script
  d.videos.push(video)
  d.scripts.push(script)
  return { video, script }
}
